import React, { useEffect, useRef, useState } from 'react';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApSimpleDropdown,
	colors,
} from '@alixpartners/ui-components';
import {
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
} from './ChartList.styles';
import { FONTSTYLE, generateToolTip, spendFormatter } from 'utils/filterNames';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getVisualViewData } from 'utils/apiRequests';
import Tooltip from 'CategoryManagement/CategoryWidget/Components/Tooltip';
import { renderToString } from 'react-dom/server';
import { keyMapping } from './utils';
import { getDateFormat } from 'CategoryManagement/utils';
import moment from 'moment';

const SplineColumnChart = ({ xAxis, modelId, skuDetails, filterData }) => {
	const { sku } = skuDetails;
	const chartRef = useRef();
	const [chartTitle, setChartTitle] = useState('');
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [filter, setFilter] = useState({
		label: 'PO Wt.Avg Unit Price',
		value: 'avgUnitPrice',
	});
	const [options, setOptions] = useState({
		chart: {
			zoomType: 'xy',
		},
		plotOptions: {
			column: {
				animation: false,
			},
			line: {
				animation: false,
			},
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: { text: '' },
		boost: {
			useGPUTranslations: true,
		},
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			crosshair: true,
			lineColor: colors.black,
			title: {
				text: xAxis,
				style: {
					...FONTSTYLE,
					textOverflow: 'ellipsis',
				},
			},
			labels: {
				style: { ...FONTSTYLE },
			},
		},
		legend: {
			enabled: true,
			symbolRadius: 1,
			itemStyle: {
				textTransform: 'uppercase',
			},
		},
		tooltip: {
			shared: false,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			borderRadius: 2,
			outside: true,
			formatter: function() {
				const { point } = this;
				const { tooltip } = point;
				const convertKeys = tooltip => {
					return tooltip.map(item => ({
						name: keyMapping[item.name] || item.name,
						value:
							typeof item.value !== 'string'
								? spendFormatter(item.value)
								: item.value,
					}));
				};
				return renderToString(<Tooltip tooltipData={convertKeys(tooltip)} />);
			},
		},
		colors: ['#032E45', '#448EB4'],
		animation: false,
	});

	useEffect(
		() => {
			if (sku !== '') {
				if (data.length === 0) {
					setIsLoading(true);
					const { dateFrom, dateTo } = getDateFormat(
						filterData.dateRange.stateRange
					);
					const payload = {
						filtersDto: {
							sku: [sku],
							category1: filterData.category_1.selectedOption,
							category2: filterData.category_2.selectedOption,
							category3: filterData.category_3.selectedOption,
							commodityCode: filterData.commodity_Code.selectedOption,
							commodityFamily: filterData.commodity_Family.selectedOption,
							originCountry: filterData.origin_Country.selectedOption,
						},
						modelId: modelId,
						startDate: dateFrom,
						endDate: dateTo,
					};
					getVisualViewData(payload)
						.then(response => {
							if (response.data.length > 0) {
								setData(response.data);
								const {
									categoriesResult,
									columnChartData,
									lineChartData,
								} = trasformAndGetResult(response.data);
								const yAxisOptions = getYAxisOptions(filter.label);
								setOptions(prevState => ({
									...prevState,
									xAxis: {
										...prevState.xAxis,
										categories: categoriesResult,
									},
									yAxis: yAxisOptions,
									series: [
										{
											name: 'Purchase Amount',
											type: 'column',
											data: columnChartData,
										},
										{
											name: filter.label,
											type: 'line',
											yAxis: 1,
											data: lineChartData,
										},
									],
								}));
								setChartTitle(
									`Multi-Price Profile for  ${
										response.data[0].sku
									}. SKU Description - ${
										response.data[0].skU_Description
									}. Date range (${moment(dateFrom).format(
										'YYYY/MM/DD'
									)}-${moment(dateTo).format('YYYY/MM/DD')})`
								);
								setIsLoading(false);
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					const {
						categoriesResult,
						columnChartData,
						lineChartData,
					} = trasformAndGetResult(data);
					const yAxisOptions = getYAxisOptions(filter.label);
					setOptions(prevState => ({
						...prevState,
						xAxis: {
							...prevState.xAxis,
							categories: categoriesResult,
						},
						yAxis: yAxisOptions,
						series: [
							{
								name: 'Purchase Amount',
								type: 'column',
								data: columnChartData,
							},
							{
								name: filter.label,
								type: 'line',
								yAxis: 1,
								data: lineChartData,
							},
						],
					}));
				}
			}
		}, // eslint-disable-next-line
		[sku, filter]
	);

	const trasformAndGetResult = data => {
		const categoriesResult = getCategories(data, 'supplier');
		const columnChartData = data.map(dataPoint => ({
			x: categoriesResult.indexOf(dataPoint.supplier),
			y: dataPoint.sumOfTotalSpend,
			tooltip: generateToolTip(dataPoint),
		}));

		const lineChartData = data.map(dataPoint => ({
			x: categoriesResult.indexOf(dataPoint.supplier),
			y: dataPoint[filter.value],
			tooltip: generateToolTip(dataPoint),
		}));

		return { categoriesResult, columnChartData, lineChartData };
	};

	const getCategories = (data, key) => {
		const categories = data.map(category => category[key]);
		return categories;
	};
	const handleFilterChange = event => {
		setFilter(event);
	};

	const getYAxisOptions = label => {
		return [
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				labels: {
					formatter: function() {
						return spendFormatter(this.value, true);
					},
				},
				title: {
					text: 'Purchase Amount',
					style: {
						...FONTSTYLE,
						textOverflow: 'ellipsis',
					},
				},
			},
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				labels: {
					formatter: function() {
						return spendFormatter(this.value, true);
					},
				},
				title: {
					text: label,
					style: {
						...FONTSTYLE,
						textOverflow: 'ellipsis',
					},
				},
				opposite: true,
			},
		];
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
			</Header>
			{isLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{data.length === 0 ? (
						<NoData>No data available</NoData>
					) : (
						<>
							<div>
								<ApSimpleDropdown
									onChange={e => handleFilterChange(e)}
									options={[
										{ label: 'PO Wt.Avg Unit Price', value: 'avgUnitPrice' },
										{ label: 'LPP (Last Price Paid)', value: 'lpp' },
									]}
									selected={filter}
									label="Filter"
								/>
							</div>
							<HighchartsReact
								highcharts={Highcharts}
								options={options}
								ref={chartRef}
							/>
						</>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

export default React.memo(SplineColumnChart);
